import { useState, useMemo, useRef, useEffect } from "react";
import {
  useCreateObject,
  useObjectsByType,
  useUpdateObject,
  objectsByTypeQueryKey,
} from "@/features/objects/hooks";
import { ModuleEmptyState } from "../_infrastructure/ModuleEmptyState";
import { useReconcileAgentEffects } from "@/app/providers/AgentEffectsContext";

import type { PlaceSuggestion } from "@/modules/places/PlaceSearchInput";
import { ModuleSearchBar } from "@/common/components/ModuleSearchBar";
import { MobileSearchToggle } from "@/common/components/MobileSearchToggle";
import { MobileActionButton } from "@/common/components/MobileActionButton";
import { apiGet } from "@/lib/api/client";
import { useSession } from "@/features/auth/hooks/useSession";
import { useToast } from "@/app/providers/ToastContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { api } from "@/lib/api";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { PlaceListLoadingView } from "./components/PlaceListLoadingView";
import { CreatingPlaceView } from "./components/CreatingPlaceView";
import { AddPlacePortal } from "./components/AddPlacePortal";
import { OptionsMenuPortal } from "./components/OptionsMenuPortal";
import { CreateFolderPortal } from "./components/CreateFolderPortal";
import { FolderContextMenu } from "./components/FolderContextMenu";
import { AddToCollectionModal } from "./components/AddToCollectionModal";
import { DeleteConfirmModal } from "./components/DeleteConfirmModal";
import { PlaceFolderGrid } from "./components/PlaceFolderGrid";
import { PlaceCardGrid } from "./components/PlaceCardGrid";
import { PlaceDetailOverlay } from "./components/PlaceDetailOverlay";
import type { PlaceObject, PlaceListModuleProps } from "./types";
export type { PlaceObject, PlaceListModuleProps } from "./types";
import { getPlaceCategory } from "./lib/placeCategory";
import { toGeo } from "./lib/placeFields";
import { usePlaceFolders } from "./hooks/usePlaceFolders";
import { usePlaceList } from "./hooks/usePlaceList";
import { usePlaceEnrichment } from "./hooks/usePlaceEnrichment";
import {
  makeHandleCreateManually,
  makeHandleDeletePlace,
  makeHandleArchiveModule,
} from "./placeHandlers";
import { useIsCompactLayout } from "@/common/hooks";
import {
  readSessionPersisted,
  writeSessionPersisted,
  useSessionPersistedState,
} from "@/common/hooks/useSessionPersistedState";
import "../_infrastructure/ModuleStyles.css";

function isPersistedFolder(
  v: unknown,
): v is { id: string; label: string } | null {
  return (
    v === null ||
    (typeof v === "object" &&
      v !== null &&
      typeof (v as { id?: unknown }).id === "string" &&
      typeof (v as { label?: unknown }).label === "string")
  );
}
const isGuidTab = (v: unknown): v is "places" | "collections" =>
  v === "places" || v === "collections";
const isPlaceSort = (v: unknown): v is "rating" | "alpha" =>
  v === "rating" || v === "alpha";
const isString = (v: unknown): v is string => typeof v === "string";
const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";

export default function PlaceListModule(props: PlaceListModuleProps) {
  const { module, conversationId: explicitConversationId } = props;
  const conversationId = explicitConversationId || module.conversation_id || "";
  const isMobile = useIsCompactLayout();
  const [form, setForm] = useState({ name: "", address: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [savingPlace, setSavingPlace] = useState<string | null>(null);

  const isPlacesContainer = module.type === "places";
  // View position (open collection folder, Places/Collections tab, sort,
  // filters, search) is persisted per module instance so returning to Places
  // after the chat pane unmounts/remounts the module (module switch) restores
  // the same first committed frame instead of resetting to defaults — mirrors
  // Kanban's activeBoard / Email's mainView restore (PR #5483).
  const folderKey = module.id ? `xavior:places.folder:${module.id}` : null;
  // currentFolder is a plain useState (not useSessionPersistedState) because
  // it needs a self-heal effect below (folder archived/deleted elsewhere) —
  // see the activeBoardId precedent in KanbanModule.tsx.
  const [currentFolder, setCurrentFolder] = useState<{
    id: string;
    label: string;
  } | null>(() => readSessionPersisted(folderKey, null, isPersistedFolder));
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const newFolderInputRef = useRef<HTMLInputElement>(null);
  const [folderMenu, setFolderMenu] = useState<{
    id: string;
    name: string;
    x: number;
    y: number;
  } | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameFolderText, setRenameFolderText] = useState("");
  const renameFolderInputRef = useRef<HTMLInputElement>(null);
  const [showAllPlaces, setShowAllPlaces] = useSessionPersistedState<boolean>(
    module.id ? `xavior:places.showAll:${module.id}` : null,
    true,
    { validate: isBoolean },
  );
  const [guidTab, setGuidTab] = useSessionPersistedState<"places" | "collections">(
    module.id ? `xavior:places.tab:${module.id}` : null,
    "places",
    { validate: isGuidTab },
  );
  const [placeSort, setPlaceSort] = useSessionPersistedState<"rating" | "alpha">(
    module.id ? `xavior:places.sort:${module.id}` : null,
    "rating",
    { validate: isPlaceSort },
  );
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [isAddingPlace, setIsAddingPlace] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const [placeMenuId, setPlaceMenuId] = useState<string | null>(null);
  const [addToCollectionPlaceId, setAddToCollectionPlaceId] = useState<
    string | null
  >(null);
  const [categoryFilter, setCategoryFilter] = useSessionPersistedState<string>(
    module.id ? `xavior:places.category:${module.id}` : null,
    "all",
    { validate: isString },
  );
  const [priceFilter, setPriceFilter] = useSessionPersistedState<string>(
    module.id ? `xavior:places.price:${module.id}` : null,
    "all",
    { validate: isString },
  );
  const [folderSearch, setFolderSearch] = useSessionPersistedState<string>(
    module.id ? `xavior:places.folderSearch:${module.id}` : null,
    "",
    { validate: isString },
  );

  useEffect(() => {
    const handleOpenOptions = (event: CustomEvent) => {
      const { moduleId } = event.detail || {};
      if (moduleId === module.id) {
        setShowOptionsMenu(true);
      }
    };
    window.addEventListener(
      "xavior:open-module-options",
      handleOpenOptions as EventListener,
    );
    return () =>
      window.removeEventListener(
        "xavior:open-module-options",
        handleOpenOptions as EventListener,
      );
  }, [module.id]);

  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showArchivedPlaces, setShowArchivedPlaces] = useState(false);

  // Listen for "View Archived" from ⋯ menu
  useEffect(() => {
    const handleToggle = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.moduleId === module.id)
        setShowArchivedPlaces((prev) => !prev);
    };
    window.addEventListener("xavior:toggle-archived-tasks", handleToggle);
    return () =>
      window.removeEventListener("xavior:toggle-archived-tasks", handleToggle);
  }, [module.id]);

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    placeId: string;
    placeName: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useSessionPersistedState<string>(
    module.id ? `xavior:places.search:${module.id}` : null,
    "",
    { validate: isString },
  );
  const optionsMenuRef = useRef<HTMLDivElement>(null);

  const { token, selectedContainer } = useSession();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const workspaceId = getActiveWorkspaceId();

  // Use same workspace ID resolution as Drive for fetching workspace modules
  const placesWorkspaceId =
    selectedContainer?.id ?? (selectedContainer as any)?.ownerId ?? workspaceId;

  // For places container: fetch workspace modules to find place_list folders
  const { data: wsModules = [], isLoading: wsModulesLoading } = useQuery({
    queryKey: queryKeys.modules.list(placesWorkspaceId),
    queryFn: () =>
      api.workspaces.listWorkspaceModules(token ?? null, placesWorkspaceId, {
        includeArchived: false,
      }),
    enabled: isPlacesContainer && Boolean(token && placesWorkspaceId),
    staleTime: 30_000,
  });

  const placeFolders = useMemo(() => {
    if (!isPlacesContainer) return [];
    return (wsModules as any[])
      .filter((m: any) => {
        if (m.id === module.id) return false;
        if (m.archivedAt) return false;
        return m.type === "place_list";
      })
      .sort((a: any, b: any) => (a.label || "").localeCompare(b.label || ""))
      .map((m: any) => ({
        id: m.id,
        label: m.label || "Untitled",
        objectCount: m.objectCount as number | undefined,
      }));
  }, [isPlacesContainer, wsModules, module.id]);

  // Self-heal a stale persisted folder (archived/deleted elsewhere, or the
  // workspace genuinely has no folders left) once the workspace-modules query
  // has settled — mirrors KanbanModule's activeBoardId precedent. Gating on
  // `!wsModulesLoading` (rather than requiring `placeFolders.length > 0`)
  // avoids wiping a valid restored folder during the one transient-empty
  // window before the query resolves, WHILE still healing against a
  // confirmed-empty folder list — the `length > 0` gate would otherwise leave
  // a stale folder id stuck forever once every collection was deleted.
  const currentFolderStale =
    Boolean(currentFolder) &&
    isPlacesContainer &&
    !wsModulesLoading &&
    !placeFolders.some((f) => f.id === currentFolder!.id);
  useEffect(() => {
    if (currentFolderStale) setCurrentFolder(null);
  }, [currentFolderStale]);
  // Persist the open folder so a remount (pane switch) reopens it synchronously.
  useEffect(() => {
    writeSessionPersisted(folderKey, currentFolder);
  }, [folderKey, currentFolder]);

  // For places container: always fetch ALL places, filter by folder client-side (instant navigation)
  // For standalone place_list: fetch by module ID
  const effectiveModuleId = isPlacesContainer ? undefined : module.id;

  const placeQueryParams = { moduleId: effectiveModuleId, limit: 500 };
  const placeQueryKey = objectsByTypeQueryKey("place", placeQueryParams);
  const { data, isLoading } = useObjectsByType<PlaceObject>("place", {
    ...(effectiveModuleId ? { moduleId: effectiveModuleId } : {}),
    limit: 500,
  });
  const createPlace = useCreateObject<any, any>(
    "place",
    conversationId,
    currentFolder?.id ?? module.id,
  );
  const updatePlace = useUpdateObject<PlaceObject>(
    "place",
    conversationId,
    currentFolder?.id ?? module.id,
  );

  const { handleCreateFolder, handleArchiveFolder, handleRenameFolder } =
    usePlaceFolders({
      renamingFolderId,
      renameFolderInputRef,
      isCreatingFolder,
      newFolderInputRef,
      newFolderName,
      token,
      placesWorkspaceId,
      queryClient,
      setIsCreatingFolder,
      setNewFolderName,
      setFolderMenu,
      setRenamingFolderId,
    });

  const allPlaces = data?.items ?? [];

  const { places, groupedFolders } = usePlaceList({
    allPlaces,
    isPlacesContainer,
    currentFolder,
    moduleId: module.id,
    showArchivedPlaces,
    searchQuery,
    placeSort,
    placeFolders,
    folderSearch,
  });

  // Suppress stale agent highlights when a newer place version is rendered.
  useReconcileAgentEffects("place", places);

  const {
    selectedPlace,
    enrichedCard,
    setEnrichedCard,
    isEnriching,
    showOverlayGearMenu,
    setShowOverlayGearMenu,
    isPublishing,
    publishedUrl,
    savedNotesRef,
    overlayGearRef,
    placeDetailRef,
    handlePlaceClick,
    handleCloseOverlay,
    handlePublishPlace,
    handleUnpublishPlace,
    handlePlaceOverlayClick,
    handlePlaceOverlayMouseDown,
  } = usePlaceEnrichment({ allPlaces, places, queryClient, placeQueryKey });

  const handleCancelCreate = () => {
    setIsCreating(false);
    setForm({ name: "", address: "" });
  };

  async function handlePlaceSelected(suggestion: PlaceSuggestion) {
    console.log(
      "[PlaceListModule] handlePlaceSelected called:",
      suggestion.primaryText,
      suggestion.placeId,
      "conversationId:",
      conversationId,
      "moduleId:",
      module.id,
    );
    setSavingPlace(suggestion.primaryText);
    try {
      const params = new URLSearchParams({ placeId: suggestion.placeId });
      const { data } = await apiGet<{
        card?: {
          title: string;
          address?: string;
          rating?: number;
          reviewCount?: number;
          phoneNumber?: string;
          imageUrl?: string;
          allImages?: Array<{
            url: string;
            attribution?: string;
            isMap?: boolean;
          }>;
          googlePlaceId?: string;
          googleMapsUri?: string;
          websiteUri?: string;
          status?: string;
          priceLevel?: string;
          description?: string;
          geo?: { lat: number; lng: number };
          sections?: Array<{ title: string; items: string[] }>;
        };
      }>(`/places/details?${params.toString()}`);

      const card = data.card;

      // Image URLs may be proxy-relative paths (e.g. /places/photo?...) or absolute URLs
      const isValidUrl = (u: any) => typeof u === "string" && u.length > 0;
      const isAbsoluteUrl = (u: any) =>
        typeof u === "string" && /^https?:\/\//.test(u);
      const resolvedImageUrl = isValidUrl(card?.imageUrl)
        ? card!.imageUrl
        : undefined;
      const resolvedImages = card?.allImages
        ?.filter((img: any) => isValidUrl(img?.url))
        .map((img: any) => ({
          url: img.url,
          ...(img.attribution ? { attribution: img.attribution } : {}),
        }));

      const linkModuleId = currentFolder?.id ?? module.id;
      const placeData: any = {
        title: card?.title || suggestion.primaryText,
        name: card?.title || suggestion.primaryText,
        address: card?.address || suggestion.secondaryText,
        googlePlaceId: card?.googlePlaceId || suggestion.placeId,
        moduleLinks: [{ moduleId: linkModuleId }],
      };
      // Only include optional fields when they have a value (avoid sending undefined)
      if (card?.rating != null) placeData.rating = card.rating;
      if (card?.reviewCount != null) placeData.reviewCount = card.reviewCount;
      if (card?.priceLevel) placeData.priceLevel = card.priceLevel;
      if (card?.phoneNumber) placeData.phoneNumber = card.phoneNumber;
      if (isAbsoluteUrl(card?.websiteUri))
        placeData.websiteUri = card!.websiteUri;
      if (isAbsoluteUrl(card?.googleMapsUri))
        placeData.googleMapsUri = card!.googleMapsUri;
      if (resolvedImageUrl) placeData.imageUrl = resolvedImageUrl;
      if (resolvedImages?.length) placeData.images = resolvedImages;
      if (card?.sections?.length) placeData.sections = card.sections;
      if (card?.description) placeData.description = card.description;
      if (card?.geo) placeData.geo = toGeo(card.geo);
      if (card?.primaryType) placeData.primaryType = card.primaryType;

      console.log(
        "[PlaceListModule] Creating place with data:",
        JSON.stringify(placeData, null, 2),
      );
      createPlace.mutate(placeData, {
        onSuccess: (newPlace: any) => {
          console.log(
            "[PlaceListModule] Place created successfully:",
            newPlace?.id,
          );
          setIsCreating(false);
          setIsAddingPlace(false);
          setSavingPlace(null);
          setForm({ name: "", address: "" });

          // Optimistically update the cache with the full enriched data
          const enrichedPlace = { ...newPlace, ...placeData };
          queryClient.setQueryData(placeQueryKey, (old: any) => {
            if (!old) return old;
            const existingIndex = old.items?.findIndex(
              (p: any) => p.id === newPlace.id,
            );
            if (existingIndex >= 0) {
              const updatedItems = [...old.items];
              updatedItems[existingIndex] = enrichedPlace;
              return { ...old, items: updatedItems };
            } else {
              return { ...old, items: [...(old.items || []), enrichedPlace] };
            }
          });

          // Server runs auto-enrichment in setImmediate after create — refetch
          // 3s later so the row picks up the Google photo / map data without
          // requiring a manual refresh. See #2443 / #2458.
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: placeQueryKey });
          }, 3000);

          handlePlaceClick(enrichedPlace as PlaceObject);
        },
        onError: (err: any) => {
          console.error(
            "[PlaceListModule] Failed to create place:",
            err?.status,
            err?.message,
            err?.payload ?? err,
          );
          setIsCreating(false);
          setSavingPlace(null);
          setForm({ name: "", address: "" });
        },
      });
    } catch (error) {
      console.error("[PlaceListModule] Failed to fetch place details:", error);
      // Fallback: create with basic info from suggestion
      const fallbackData = {
        title: suggestion.primaryText,
        name: suggestion.primaryText,
        address: suggestion.secondaryText || "",
        googlePlaceId: suggestion.placeId,
        moduleLinks: [{ moduleId: currentFolder?.id ?? module.id }],
      };
      createPlace.mutate(fallbackData, {
        onSuccess: (newPlace: any) => {
          setIsCreating(false);
          setIsAddingPlace(false);
          setSavingPlace(null);
          setForm({ name: "", address: "" });
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: placeQueryKey });
          }, 3000);
          handlePlaceClick({ ...newPlace, ...fallbackData } as PlaceObject);
        },
        onError: (err: any) => {
          console.error("[PlaceListModule] Fallback create also failed:", err);
          setIsCreating(false);
          setIsAddingPlace(false);
          setSavingPlace(null);
          setForm({ name: "", address: "" });
        },
      });
    }
  }

  const handleCreateManually = makeHandleCreateManually({
    form,
    createPlace,
    currentFolder,
    moduleId: module.id,
    queryClient,
    placeQueryKey,
    setForm,
    setIsCreating,
  });

  const handleDeletePlace = makeHandleDeletePlace({
    token,
    queryClient,
    placeQueryKey,
    setDeleteConfirmation,
    showToast,
  });

  const handleArchiveModule = makeHandleArchiveModule({
    setShowOptionsMenu,
    workspaceId,
    conversationId,
    token,
    moduleId: module.id,
    queryClient,
  });

  if (isLoading) {
    return <PlaceListLoadingView />;
  }

  // Creating new place view
  if (isCreating) {
    return (
      <CreatingPlaceView
        handleCancelCreate={handleCancelCreate}
        handleCreateManually={handleCreateManually}
        form={form}
        handlePlaceSelected={handlePlaceSelected}
      />
    );
  }

  return (
    <div className="module module-places">
      <div
        className="module-accent-rail"
        style={{ background: "var(--color-border)" }}
      />
      {/* New Place modal — rendered at root so it works from any view (incl. empty state in showAllPlaces) */}
      {isAddingPlace && (
        <AddPlacePortal
          onClose={() => setIsAddingPlace(false)}
          onPlaceSelected={handlePlaceSelected}
        />
      )}
      {showArchivedPlaces && (
        <div className="module-header">
          <button
            onClick={() => setShowArchivedPlaces(false)}
            style={{
              padding: "0 10px",
              height: 36,
              background: "rgba(var(--color-primary-rgb), 0.12)",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              color: "var(--color-primary-hover)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(var(--color-primary-rgb), 0.18)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(var(--color-primary-rgb), 0.12)")
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Exit Archive
          </button>
        </div>
      )}

      {showOptionsMenu && (
        <OptionsMenuPortal
          isMobile={isMobile}
          showArchivedPlaces={showArchivedPlaces}
          optionsMenuRef={optionsMenuRef}
          onClose={() => setShowOptionsMenu(false)}
          onToggleArchived={() => {
            setShowArchivedPlaces(!showArchivedPlaces);
            setShowOptionsMenu(false);
          }}
          onArchiveModule={() => {
            handleArchiveModule();
            setShowOptionsMenu(false);
          }}
        />
      )}
      {/* Tab bar: Places | Collections */}
      {isPlacesContainer && !currentFolder && (
        <div className="module-nav">
          <div className="module-nav__primary">
            <div className="module-toolbar-button-group">
              <button
                type="button"
                className={`module-toolbar-button ${guidTab === "places" ? "is-active" : ""}`}
                onClick={() => {
                  setGuidTab("places");
                  setShowAllPlaces(true);
                  setCategoryFilter("all");
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span className="module-toolbar-label" data-text="Places">
                  Places
                </span>
              </button>
              <button
                type="button"
                className={`module-toolbar-button ${guidTab === "collections" ? "is-active" : ""}`}
                onClick={() => {
                  setGuidTab("collections");
                  setShowAllPlaces(false);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span className="module-toolbar-label" data-text="Collections">
                  Collections
                </span>
              </button>
            </div>
          </div>
          <div className="module-nav__secondary">
            <div className="module-toolbar-button-group">
              <button
                type="button"
                className={`module-toolbar-button ${placeSort === "rating" ? "is-active" : ""}`}
                onClick={() => setPlaceSort("rating")}
                title="Sort by rating"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <span className="module-toolbar-label" data-text="Rating">
                  Rating
                </span>
              </button>
              <button
                type="button"
                className={`module-toolbar-button ${placeSort === "alpha" ? "is-active" : ""}`}
                onClick={() => setPlaceSort("alpha")}
                title="Sort A-Z"
              >
                <span className="module-toolbar-label" data-text="A-Z">
                  A-Z
                </span>
              </button>
            </div>
            <MobileSearchToggle targetId="places-search-toolbar" />
            <MobileActionButton
              onClick={() => setIsAddingPlace(true)}
              title="Add place"
            />
          </div>
        </div>
      )}
      {isPlacesContainer && !currentFolder && guidTab === "collections" && (
        <div
          id="places-search-toolbar"
          className="module-toolbar module-search-row"
        >
          <ModuleSearchBar
            standalone={false}
            value={folderSearch}
            onChange={setFolderSearch}
            onClose={() => setFolderSearch("")}
            placeholder="Search collections..."
            autoFocus={false}
          />
          <button
            className="module-toolbar-button is-primary"
            onClick={() => {
              setIsCreatingFolder(true);
              setNewFolderName("");
            }}
            title="Add collection"
          >
            + New
          </button>
        </div>
      )}
      {isPlacesContainer && guidTab === "places" && !currentFolder && (
        <div
          id="places-search-toolbar"
          className="module-toolbar module-search-row"
        >
          <ModuleSearchBar
            standalone={false}
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={() => setSearchQuery("")}
            placeholder="Search places..."
            resultCount={searchQuery ? places.length : undefined}
            autoFocus={false}
          />
          <div ref={newMenuRef} style={{ position: "relative" }}>
            <button
              className="module-toolbar-button is-primary"
              onClick={() => setShowNewMenu(!showNewMenu)}
              title="Add new"
            >
              + New
            </button>
            {showNewMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 4,
                  background: "var(--color-surface)",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  boxShadow: "var(--popover-shadow, 0 4px 12px rgba(0,0,0,0.1))",
                  padding: 4,
                  zIndex: 50,
                  minWidth: 160,
                }}
              >
                <button
                  onClick={() => {
                    setIsAddingPlace(true);
                    setShowNewMenu(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    borderRadius: 4,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f1f5f9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  New Place
                </button>
                <button
                  onClick={() => {
                    setIsCreatingFolder(true);
                    setNewFolderName("");
                    setShowNewMenu(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    borderRadius: 4,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f1f5f9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  New Collection
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {isPlacesContainer && currentFolder && !showAllPlaces && (
        <div
          id="places-search-toolbar"
          className="module-toolbar module-search-row"
        >
          <ModuleSearchBar
            standalone={false}
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={() => setSearchQuery("")}
            placeholder="Search places..."
            resultCount={searchQuery ? places.length : undefined}
            autoFocus={false}
          />
          <button
            className="module-toolbar-button is-primary"
            onClick={() => setIsAddingPlace(true)}
            title="Add place"
          >
            + New
          </button>
        </div>
      )}
      {/* Empty states — outside module-content for consistent position */}
      {!showArchivedPlaces &&
        isPlacesContainer &&
        !currentFolder &&
        guidTab === "collections" &&
        groupedFolders.length === 0 &&
        !folderSearch &&
        !isLoading &&
        !isCreatingFolder && (
          <ModuleEmptyState
            icon={
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22c55e"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            }
            iconColor="#22c55e"
            title="AI-powered locations"
            description={
              <>
                Save hotels, restaurants, and landmarks — organized by
                collection.
                <br />
                Xavior remembers your favorites and helps plan trips and
                itineraries.
              </>
            }
            buttonLabel="Add a collection"
            onButtonClick={() => {
              setIsCreatingFolder(true);
              setNewFolderName("");
            }}
          />
        )}
      <div className="module-content">
        {isPlacesContainer && !currentFolder && guidTab === "collections" && (
          <>
            {isCreatingFolder && (
              <CreateFolderPortal
                newFolderName={newFolderName}
                onChangeName={setNewFolderName}
                onCancel={() => {
                  setIsCreatingFolder(false);
                  setNewFolderName("");
                }}
                onCreate={() => {
                  handleCreateFolder();
                  setIsCreatingFolder(false);
                  setGuidTab("collections");
                  setShowAllPlaces(false);
                }}
              />
            )}

            <PlaceFolderGrid
              placeFolders={placeFolders}
              allPlaces={allPlaces}
              groupedFolders={groupedFolders}
              folderSearch={folderSearch}
              renamingFolderId={renamingFolderId}
              renameFolderText={renameFolderText}
              renameFolderInputRef={renameFolderInputRef}
              folderMenu={folderMenu}
              setCurrentFolder={setCurrentFolder}
              setRenameFolderText={setRenameFolderText}
              setRenamingFolderId={setRenamingFolderId}
              setFolderMenu={setFolderMenu}
              handleRenameFolder={handleRenameFolder}
              handlePlaceClick={handlePlaceClick}
            />

            {folderMenu && (
              <FolderContextMenu
                menu={folderMenu}
                onClose={() => setFolderMenu(null)}
                onRename={() => {
                  setRenamingFolderId(folderMenu.id);
                  setRenameFolderText(folderMenu.name);
                  setFolderMenu(null);
                }}
                onArchive={() => handleArchiveFolder(folderMenu.id)}
              />
            )}
          </>
        )}

        {isPlacesContainer && guidTab === "places" && !currentFolder && (
          <>
            {isCreatingFolder && guidTab === "places" && (
              <CreateFolderPortal
                newFolderName={newFolderName}
                onChangeName={setNewFolderName}
                onCancel={() => {
                  setIsCreatingFolder(false);
                  setNewFolderName("");
                }}
                onCreate={() => {
                  handleCreateFolder();
                  setIsCreatingFolder(false);
                  setGuidTab("collections");
                  setShowAllPlaces(false);
                }}
              />
            )}
            {(() => {
              const categories = Array.from(
                new Set(places.map((p) => getPlaceCategory(p.primaryType, p))),
              ).sort();
              if (categories.length <= 1) return null;
              return (
                <div
                  className="module-toolbar-button-group"
                  style={{
                    margin: isMobile ? "2px 8px 4px" : "4px 12px 8px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className={`module-toolbar-button ${categoryFilter === "all" ? "is-active" : ""}`}
                    onClick={() => setCategoryFilter("all")}
                  >
                    All ({places.length})
                  </button>
                  {categories.map((cat) => {
                    const count = places.filter(
                      (p) => getPlaceCategory(p.primaryType, p) === cat,
                    ).length;
                    return (
                      <button
                        key={cat}
                        type="button"
                        className={`module-toolbar-button ${categoryFilter === cat ? "is-active" : ""}`}
                        onClick={() => setCategoryFilter(cat)}
                      >
                        {cat} ({count})
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}

        {isPlacesContainer && currentFolder && !showAllPlaces && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 16px",
                borderBottom:
                  "1px solid var(--color-border-light, rgba(0,0,0,0.06))",
              }}
            >
              <div
                onClick={() => setCurrentFolder(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--color-text-muted, #666)",
                  fontFamily: "'DM Sans', sans-serif",
                  flex: 1,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                {currentFolder.label}
                {places.length > 0 ? ` (${places.length})` : ""}
              </div>
            </div>
            {/* New Place modal hoisted to root return so it works from every view */}
          </>
        )}

        {/* Place list (shown when inside a folder, in "All" mode, or when this is a standalone place_list module) */}
        {(!isPlacesContainer || currentFolder || showAllPlaces) && (
          <>
            {!isMobile &&
              (() => {
                const hasMultiplePrices = places.some((p) => p.priceLevel);
                if (!hasMultiplePrices) return null;
                return (
                  <div
                    className="module-toolbar"
                    style={{
                      borderBottom:
                        "1px solid var(--color-border-light, rgba(0,0,0,0.06))",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "var(--color-text-muted, #999)",
                        marginRight: 4,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      Max price
                    </span>
                    {["all", "$", "$$", "$$$", "$$$$"].map((p) => (
                      <button
                        key={p}
                        onClick={() => setPriceFilter(p)}
                        style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 500,
                          border:
                            priceFilter === p
                              ? "none"
                              : "1px solid var(--color-border, #ddd)",
                          cursor: "pointer",
                          fontFamily: "'DM Sans', sans-serif",
                          background:
                            priceFilter === p
                              ? "var(--color-primary, #1a1a1a)"
                              : "transparent",
                          color:
                            priceFilter === p
                              ? "#fff"
                              : "var(--color-text-muted, #666)",
                        }}
                      >
                        {p === "all" ? "All" : p}
                      </button>
                    ))}
                  </div>
                );
              })()}
            {places.length === 0 ? (
              showArchivedPlaces ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "var(--color-text-faint)",
                    fontSize: 13,
                  }}
                >
                  No archived places
                </div>
              ) : searchQuery ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "48px 20px",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--color-text-primary)",
                      marginBottom: 4,
                    }}
                  >
                    No places matching &ldquo;{searchQuery}&rdquo;
                  </div>
                </div>
              ) : (
                <ModuleEmptyState
                  icon={
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  }
                  iconColor="#22c55e"
                  title="AI-powered locations"
                  description={
                    <>
                      Save hotels, restaurants, and landmarks — organized by
                      collection.
                      <br />
                      Xavior remembers your favorites and helps plan trips and
                      itineraries.
                    </>
                  }
                  buttonLabel="Add a place"
                  onButtonClick={() => setIsAddingPlace(true)}
                />
              )
            ) : (
              <PlaceCardGrid
                places={places}
                placeFolders={placeFolders}
                showAllPlaces={showAllPlaces}
                categoryFilter={categoryFilter}
                priceFilter={priceFilter}
                isMobile={isMobile}
                showOptionsMenu={showOptionsMenu}
                placeMenuId={placeMenuId}
                showArchivedPlaces={showArchivedPlaces}
                updatePlace={updatePlace}
                handlePlaceClick={handlePlaceClick}
                setPlaceMenuId={setPlaceMenuId}
                setAddToCollectionPlaceId={setAddToCollectionPlaceId}
                handleDeletePlace={handleDeletePlace}
              />
            )}
          </>
        )}
      </div>

      {addToCollectionPlaceId && (
        <AddToCollectionModal
          placeId={addToCollectionPlaceId}
          allPlaces={allPlaces}
          placeFolders={placeFolders}
          onClose={() => setAddToCollectionPlaceId(null)}
          onMove={async (folderId, thePlace, isCurrentCollection) => {
            if (isCurrentCollection) {
              setAddToCollectionPlaceId(null);
              return;
            }
            try {
              // New moduleLinks replace the old collection link.
              await api.objects.updateObject(
                token ?? null,
                "place",
                addToCollectionPlaceId!,
                {
                  moduleLinks: [{ moduleId: folderId }],
                } as any,
                thePlace?._etag,
              );
              setAddToCollectionPlaceId(null);
              queryClient.invalidateQueries({
                queryKey: ["objects", "byType", "place"],
              });
            } catch (err) {
              console.error("Failed to move place:", err);
              showToast("Failed to move place to collection", 'error');
            }
          }}
        />
      )}

      {selectedPlace && (
        <PlaceDetailOverlay
          selectedPlace={selectedPlace}
          enrichedCard={enrichedCard}
          isEnriching={isEnriching}
          isPublishing={isPublishing}
          publishedUrl={publishedUrl}
          showOverlayGearMenu={showOverlayGearMenu}
          module={module}
          conversationId={conversationId}
          placeDetailRef={placeDetailRef}
          overlayGearRef={overlayGearRef}
          onOverlayMouseDown={handlePlaceOverlayMouseDown}
          onOverlayClick={handlePlaceOverlayClick}
          onClose={handleCloseOverlay}
          onToggleGearMenu={() => setShowOverlayGearMenu(!showOverlayGearMenu)}
          onPublish={handlePublishPlace}
          onUnpublish={handleUnpublishPlace}
          onCopyPublishedLink={() => {
            const url = publishedUrl || (selectedPlace as any)?.publish?.url;
            if (url) navigator.clipboard.writeText(url).catch(() => {});
            setShowOverlayGearMenu(false);
          }}
          onDelete={() => {
            setShowOverlayGearMenu(false);
            if (selectedPlace) {
              setDeleteConfirmation({
                placeId: selectedPlace.id,
                placeName: selectedPlace.title || selectedPlace.name,
              });
              handleCloseOverlay();
            }
          }}
          onNoteSaved={(noteText: string) => {
            setEnrichedCard((prev: any) =>
              prev ? { ...prev, userNotes: noteText } : prev,
            );
            if (selectedPlace) {
              savedNotesRef.current[selectedPlace.id] = noteText;
            }
          }}
        />
      )}

      {deleteConfirmation && (
        <DeleteConfirmModal
          onCancel={() => setDeleteConfirmation(null)}
          onConfirm={() => handleDeletePlace(deleteConfirmation.placeId)}
        />
      )}
    </div>
  );
}
